import React, { useState, useRef, useEffect } from 'react';
import { legalDocuments, LEGAL_VERSION, LEGAL_EFFECTIVE_DATE } from './legalContent';

/* ─────────────────────────────────────────────────────────────────────────
   LegalDocuments
   A self-contained, tabbed reader for Restropilots' full legal pack
   (Privacy Policy, Terms of Service, DPA, Security Overview, NDA).

   Usage:
     <LegalDocuments
       open={showLegal}
       onClose={() => setShowLegal(false)}
       initialTab="terms"              // optional, defaults to "terms"
       onAccept={() => setAgreed(true)} // optional — shows an Accept button
     />

   Renders nothing when `open` is false. Designed to sit on top of the
   Partner.jsx auth screen (same dark / amber design language) but has no
   dependency on it — drop it in anywhere.
─────────────────────────────────────────────────────────────────────────── */

const css = `
  .ld-overlay {
    position: fixed;
    inset: 0;
    background: rgba(6,6,9,0.72);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: clamp(12px, 3vw, 32px);
    font-family: 'DM Sans', system-ui, sans-serif;
    animation: ldFadeIn 0.18s ease-out;
  }
  @keyframes ldFadeIn { from { opacity: 0; } to { opacity: 1; } }

  .ld-modal {
    width: 100%;
    max-width: 820px;
    height: min(88vh, 820px);
    background: #13131a;
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 18px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: ldSlideUp 0.22s cubic-bezier(0.4,0,0.2,1);
    box-shadow: 0 24px 60px rgba(0,0,0,0.5);
  }
  @keyframes ldSlideUp { from { opacity: 0; transform: translateY(14px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }

  .ld-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 20px 24px 0;
    flex-shrink: 0;
  }
  .ld-title-wrap { display: flex; flex-direction: column; gap: 2px; }
  .ld-eyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #f59e0b;
  }
  .ld-title {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: 22px;
    font-weight: 600;
    color: #f0ede8;
    letter-spacing: -0.01em;
  }
  .ld-close {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(240,237,232,0.6);
    width: 30px; height: 30px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 15px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    transition: all 0.15s;
  }
  .ld-close:hover { background: rgba(255,255,255,0.1); color: #f0ede8; }

  .ld-notice {
    margin: 14px 24px 0;
    padding: 10px 13px;
    background: rgba(245,158,11,0.1);
    border: 1px solid rgba(245,158,11,0.22);
    border-radius: 9px;
    font-size: 11.5px;
    line-height: 1.5;
    color: #fbbf24;
    flex-shrink: 0;
  }

  .ld-tabs {
    display: flex;
    gap: 4px;
    padding: 14px 24px 0;
    overflow-x: auto;
    flex-shrink: 0;
    scrollbar-width: none;
  }
  .ld-tabs::-webkit-scrollbar { display: none; }
  .ld-tab {
    padding: 8px 13px;
    font-size: 12.5px;
    font-weight: 500;
    color: rgba(240,237,232,0.45);
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px 8px 0 0;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s;
    font-family: 'DM Sans', system-ui, sans-serif;
  }
  .ld-tab:hover { color: rgba(240,237,232,0.75); }
  .ld-tab.active {
    color: #f59e0b;
    background: rgba(245,158,11,0.1);
    border-color: rgba(245,158,11,0.22);
    border-bottom-color: transparent;
    font-weight: 600;
  }

  .ld-divider { height: 1px; background: rgba(255,255,255,0.08); flex-shrink: 0; }

  .ld-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px 24px 8px;
  }
  .ld-body::-webkit-scrollbar { width: 8px; }
  .ld-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 99px; }
  .ld-body::-webkit-scrollbar-track { background: transparent; }

  .ld-doc-meta {
    font-size: 11.5px;
    color: rgba(240,237,232,0.4);
    margin-bottom: 18px;
    font-weight: 500;
  }

  .ld-section { margin-bottom: 22px; }
  .ld-h { font-size: 14px; font-weight: 700; color: #f0ede8; margin-bottom: 8px; letter-spacing: -0.01em; }
  .ld-p { font-size: 13px; line-height: 1.7; color: rgba(240,237,232,0.7); margin-bottom: 8px; font-weight: 300; }
  .ld-ul { list-style: none; margin: 0 0 4px; padding: 0; display: flex; flex-direction: column; gap: 7px; }
  .ld-li {
    font-size: 13px;
    line-height: 1.6;
    color: rgba(240,237,232,0.7);
    font-weight: 300;
    padding-left: 16px;
    position: relative;
  }
  .ld-li::before {
    content: '';
    position: absolute;
    left: 0; top: 7px;
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #f59e0b;
  }

  .ld-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid rgba(255,255,255,0.08);
    flex-shrink: 0;
  }
  .ld-footer-note { font-size: 11px; color: rgba(240,237,232,0.35); }
  .ld-footer-actions { display: flex; gap: 10px; }

  .ld-btn {
    padding: 10px 18px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    border: none;
    font-family: 'DM Sans', system-ui, sans-serif;
    transition: all 0.15s;
  }
  .ld-btn-ghost {
    background: transparent;
    color: rgba(240,237,232,0.6);
    border: 1px solid rgba(255,255,255,0.12);
  }
  .ld-btn-ghost:hover { background: rgba(255,255,255,0.05); color: #f0ede8; }
  .ld-btn-primary {
    background: #f59e0b;
    color: #0a0a0f;
  }
  .ld-btn-primary:hover { background: #fbbf24; }

  @media (max-width: 560px) {
    .ld-modal { height: 92vh; border-radius: 14px; }
    .ld-title { font-size: 18px; }
    .ld-footer { flex-direction: column-reverse; align-items: stretch; }
    .ld-footer-actions { justify-content: stretch; }
    .ld-btn { flex: 1; }
  }
`;

export default function LegalDocuments({ open, onClose, initialTab = 'terms', onAccept }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (open) setActiveTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [activeTab]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const doc = legalDocuments.find(d => d.id === activeTab) || legalDocuments[0];

  return (
    <>
      <style>{css}</style>
      <div className="ld-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
        <div className="ld-modal" role="dialog" aria-modal="true" aria-label="Restropilots Legal Documents">

          <div className="ld-header">
            <div className="ld-title-wrap">
              <span className="ld-eyebrow">Legal & Compliance</span>
              <h2 className="ld-title">Restropilots Document Pack</h2>
            </div>
            <button className="ld-close" onClick={onClose} aria-label="Close">✕</button>
          </div>

          <div className="ld-notice">
            Version {LEGAL_VERSION} · Effective {LEGAL_EFFECTIVE_DATE}. This pack is a business draft
            reviewed for operational accuracy; it is not a substitute for independent legal advice.
          </div>

          <div className="ld-tabs">
            {legalDocuments.map(d => (
              <button
                key={d.id}
                className={`ld-tab${activeTab === d.id ? ' active' : ''}`}
                onClick={() => setActiveTab(d.id)}
              >
                {d.title}
              </button>
            ))}
          </div>

          <div className="ld-divider" />

          <div className="ld-body" ref={bodyRef}>
            <p className="ld-doc-meta">{doc.meta}</p>
            {doc.sections.map((s, i) => (
              <div className="ld-section" key={i}>
                <h3 className="ld-h">{s.heading}</h3>
                {s.paragraphs?.map((p, j) => (
                  <p className="ld-p" key={`p-${j}`}>{p}</p>
                ))}
                {s.bullets && (
                  <ul className="ld-ul">
                    {s.bullets.map((b, j) => (
                      <li className="ld-li" key={`b-${j}`}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="ld-footer">
            <span className="ld-footer-note">restropilots.com · director@restropilots.co.in</span>
            <div className="ld-footer-actions">
              <button className="ld-btn ld-btn-ghost" onClick={onClose}>Close</button>
              {onAccept && (
                <button
                  className="ld-btn ld-btn-primary"
                  onClick={() => { onAccept(); onClose?.(); }}
                >
                  I Accept — Close
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}